import type { QRConfig } from "@/pages/generator/page";
import TemplateSelector, { type QRTemplate } from "./TemplateSelector";

interface Props {
  config: QRConfig;
  onChange: (partial: Partial<QRConfig>) => void;
  onBack: () => void;
}

const colorPresets = [
  "#111827", "#10b981", "#0f766e", "#0891b2", "#7c3aed", "#dc2626", "#ea580c", "#ca8a04",
];

export default function QRDesignPanel({ config, onChange, onBack }: Props) {
  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Tasarımı Özelleştirin</h2>
      <p className="text-sm text-gray-500 mb-6">QR kodunuzun rengini, şablonunu ve metinlerini ayarlayın.</p>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
        {/* Template Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Şablon</label>
          <TemplateSelector
            selected={config.template}
            onSelect={(template: QRTemplate) => onChange({ template })}
          />
        </div>

        {/* Text Customization */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">Metin İçerikleri</label>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Ana Başlık (QR üzerinde)</label>
            <input
              type="text"
              value={config.qrName}
              onChange={(e) => onChange({ qrName: e.target.value })}
              placeholder="Örn: Menü QR Kodu"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Alt Metin</label>
            <input
              type="text"
              value={config.qrSubtext}
              onChange={(e) => onChange({ qrSubtext: e.target.value })}
              placeholder="Örn: Taramak için karekodu okutun"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Marka / Logo Yazısı</label>
            <input
              type="text"
              value={config.brandText}
              onChange={(e) => onChange({ brandText: e.target.value })}
              placeholder="Örn: QRGo"
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400 transition-colors"
            />
          </div>
        </div>

        {/* Foreground Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">QR Kod Rengi</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {colorPresets.map((color) => (
              <button
                key={color}
                onClick={() => onChange({ fgColor: color })}
                className={`w-8 h-8 rounded-full border-2 transition-all cursor-pointer ${
                  config.fgColor === color ? "border-gray-400 scale-110" : "border-transparent"
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={config.fgColor}
              onChange={(e) => onChange({ fgColor: e.target.value })}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
            />
            <input
              type="text"
              value={config.fgColor}
              onChange={(e) => onChange({ fgColor: e.target.value })}
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-emerald-400"
            />
          </div>
        </div>

        {/* Background Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Arka Plan Rengi</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={config.bgColor}
              onChange={(e) => onChange({ bgColor: e.target.value })}
              className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
            />
            <input
              type="text"
              value={config.bgColor}
              onChange={(e) => onChange({ bgColor: e.target.value })}
              className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-emerald-400"
            />
          </div>
        </div>

        {/* Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Boyut: <span className="text-emerald-600 font-bold">{config.size}px</span>
          </label>
          <input
            type="range"
            min={150}
            max={600}
            step={50}
            value={config.size}
            onChange={(e) => onChange({ size: Number(e.target.value) })}
            className="w-full accent-emerald-500 cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>150px</span>
            <span>600px</span>
          </div>
        </div>

        {/* Error Correction */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Hata Düzeltme Seviyesi</label>
          <div className="grid grid-cols-4 gap-2">
            {(["L", "M", "Q", "H"] as const).map((level) => (
              <button
                key={level}
                onClick={() => onChange({ errorLevel: level })}
                className={`py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                  config.errorLevel === level
                    ? "bg-emerald-500 text-white"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {level}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            H seviyesi logo eklemek için önerilir.
          </p>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-full hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Geri
        </button>
      </div>
    </div>
  );
}
